import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useMoneyFmt, useDateFmt } from "@/lib/company";
import { useCompany } from "@/lib/company";
import { TID } from "@/constants/testIds";
import { Plus, Trash2, X, AlertTriangle, Pencil, Repeat, Check, Search } from "lucide-react";
import { toast } from "sonner";
import { useCreateListener, useActionListener } from "@/lib/createBus";
import MonthCloseBreadcrumb from "@/components/MonthCloseBreadcrumb";
import MemorizeModal from "@/components/MemorizeModal";
import ItemPicker from "@/components/ItemPicker";

const BUCKETS = [
  { key: "current", label: "Current", desc: "Not yet due", color: "emerald" },
  { key: "1_30", label: "1–30 days", desc: "Past due", color: "amber" },
  { key: "31_60", label: "31–60 days", desc: "Late", color: "orange" },
  { key: "61_90", label: "61–90 days", desc: "Very late", color: "red" },
  { key: "over_90", label: "90+ days", desc: "Critical", color: "rose" },
];
const BAR = { emerald: "bg-emerald-500", amber: "bg-amber-500", orange: "bg-orange-500", red: "bg-red-500", rose: "bg-rose-600" };
const TEXT = { emerald: "text-emerald-700", amber: "text-amber-700", orange: "text-orange-700", red: "text-red-700", rose: "text-rose-700" };
const BG = { emerald: "bg-emerald-50 border-emerald-100", amber: "bg-amber-50 border-amber-100", orange: "bg-orange-50 border-orange-100", red: "bg-red-50 border-red-100", rose: "bg-rose-50 border-rose-100" };

export default function Bills() {
  const { currentId } = useCompany();
  const fmtMoney = useMoneyFmt();
  const fmtDate = useDateFmt();
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [itemsCatalog, setItemsCatalog] = useState([]);
  const [aging, setAging] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creatingPrefill, setCreatingPrefill] = useState(null);
  const [editing, setEditing] = useState(null);
  const [memorizing, setMemorizing] = useState(null);
  const [numEditId, setNumEditId] = useState(null);
  const [numEditVal, setNumEditVal] = useState("");
  const commitNumberEdit = async (bill) => {
    const val = (numEditVal || "").trim();
    if (!val || val === bill.number) { setNumEditId(null); return; }
    try {
      const r = await api.patch(`/companies/${currentId}/bills/${bill.id}`, { number: val });
      if (r.data?.number_conflict) {
        toast.warning(`Heads up — another bill already uses ${val}.`);
      } else {
        toast.success("Bill number updated");
      }
      setNumEditId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  // Deep-link filters from Month Close: /bills?outstanding=1&as_of=YYYY-MM-DD
  // A/P Aging bucket click-through: /bills?bucket=<key>
  // Highlights tile click-through:   /bills?overdue=1  (strictly late, bal>0)
  const [params, setParams] = useSearchParams();
  const outstanding = params.get("outstanding") === "1";
  const overdue = params.get("overdue") === "1";
  const asOf = params.get("as_of") || "";
  const bucket = params.get("bucket") || "";
  // Highlights vs A/P Aging card view — persisted so the CPA's last
  // pick sticks across reloads (mirrors the Invoices A/R Aging toggle).
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("ap_aging_view") || "highlights"; }
    catch { return "highlights"; }
  });
  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem("ap_aging_view", v); } catch { /* ignore */ }
  };
  // Client-side text search — filters against number/vendor/status/amount.
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (view === "paid") {
      // "Paid" mode — bills fully settled. Ignore aging/bucket/overdue
      // URL filters; still respect the search text.
      return items.filter(b => {
        const isPaid = (b.status === "paid") || (Number(b.balance_due) <= 0.005);
        if (!isPaid) return false;
        if (q) {
          const hay = [b.number, b.contact_name, b.vendor_name, b.status, b.total, b.balance_due]
            .map((v) => String(v ?? "").toLowerCase()).join(" ");
          if (!hay.includes(q)) return false;
        }
        return true;
      });
    }
    if (!outstanding && !overdue && !asOf && !bucket && !q) return items;
    const today = new Date().toISOString().slice(0, 10);
    const daysLate = (due) => {
      if (!due) return -1;
      const ms = new Date(today).getTime() - new Date(due).getTime();
      return Math.floor(ms / 86400000);
    };
    return items.filter(b => {
      if (outstanding && !(Number(b.balance_due) > 0.005)) return false;
      if (overdue) {
        if (!(Number(b.balance_due) > 0.005)) return false;
        if (!(daysLate(b.due_date) >= 1)) return false;
      }
      if (bucket) {
        if (!(Number(b.balance_due) > 0.005)) return false;
        const dl = daysLate(b.due_date);
        if (bucket === "current"  && !(dl <= 0)) return false;
        if (bucket === "1_30"     && !(dl >= 1  && dl <= 30)) return false;
        if (bucket === "31_60"    && !(dl >= 31 && dl <= 60)) return false;
        if (bucket === "61_90"    && !(dl >= 61 && dl <= 90)) return false;
        if (bucket === "over_90"  && !(dl >  90)) return false;
      }
      if (asOf) {
        const d = b.issue_date || b.date || "";
        if (d && d > asOf) return false;
      }
      if (q) {
        const hay = [
          b.number, b.contact_name, b.vendor_name, b.status,
          b.total, b.balance_due,
        ].map((v) => String(v ?? "").toLowerCase()).join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, outstanding, overdue, asOf, bucket, search, view]);
  const clearFilters = () => {
    const p = new URLSearchParams(params);
    p.delete("outstanding"); p.delete("overdue"); p.delete("as_of"); p.delete("bucket");
    setParams(p, { replace: true });
  };
  const onBucketClick = (k) => {
    const p = new URLSearchParams(params);
    if (bucket === k) p.delete("bucket");
    else p.set("bucket", k);
    setParams(p, { replace: true });
  };
  const load = async () => {
    if (!currentId) return;
    const [b, c, a, it] = await Promise.all([
      api.get(`/companies/${currentId}/bills`),
      api.get(`/companies/${currentId}/contacts`),
      api.get(`/companies/${currentId}/reports/ap-aging`),
      api.get(`/companies/${currentId}/items?usage=purchases`),
    ]);
    setItems(b.data.bills || []); setContacts(c.data.contacts || []); setAging(a.data);
    setItemsCatalog(it.data.items || []);
  };
  useEffect(() => { load(); }, [currentId]);
  useCreateListener("bill", (prefill) => {
    setCreatingPrefill(prefill || {});
    setCreating(true);
  });
  useActionListener("close-current-modal", () => {
    setCreating(false);
    setCreatingPrefill(null);
    setEditing(null);
    load();
  });
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/companies/${currentId}/bills/${id}`); load(); } };

  return (
    <div className="space-y-4">
      <MonthCloseBreadcrumb />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Bills</h1>
          <p className="text-slate-500 text-sm mt-1">Money out · vendor bills to be paid.</p>
        </div>
        <button data-testid={TID.addBtn} onClick={() => navigate("/bills/new")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs">
          <Plus size={13} /> New Bill
        </button>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <Link to="/recurring" data-testid="recurring-link" className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
          <Repeat size={12} /> Recurring bills
        </Link>
      </div>

      {aging && aging.total > 0 && (
        <div data-testid="ap-aging-widget" className="rounded-xl border bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-heading font-semibold">
                {view === "paid" ? "Paid" : view === "highlights" ? "Highlights" : "A/P Aging"}
              </div>
              <div className="text-xs text-slate-500">
                {view === "paid"
                  ? <>Bills already settled · <span className="font-mono-num font-semibold text-slate-800">{filtered.length}</span> shown</>
                  : view === "highlights"
                  ? <>As of {aging.as_of} · <span className="font-mono-num font-semibold text-slate-800">{fmtMoney(aging.total)}</span> outstanding</>
                  : <>Outstanding payables as of {aging.as_of} · <span className="font-mono-num font-semibold text-slate-800">{fmtMoney(aging.total)}</span> total</>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(aging.buckets["61_90"] + aging.buckets["over_90"]) > 0 && view === "aging" && (
                <div className="text-xs px-2 py-1 rounded-md bg-red-50 border border-red-200 text-red-700 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  {fmtMoney(aging.buckets["61_90"] + aging.buckets["over_90"])} severely late
                </div>
              )}
              {/* Highlights ↔ A/P Aging ↔ Paid toggle — mirrors Invoices'
                  A/R Aging toggle so bills has feature-parity. Persisted
                  to localStorage `ap_aging_view`. Selecting Paid filters
                  the list to bills with status=paid / balance_due=0 and
                  hides the aging widget body. */}
              <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="ap-aging-toggle">
                <button
                  type="button"
                  onClick={() => setViewPersist("highlights")}
                  className={`text-xs font-semibold px-2.5 py-1 rounded ${
                    view === "highlights" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid="ap-aging-toggle-highlights"
                >
                  Highlights
                </button>
                <button
                  type="button"
                  onClick={() => setViewPersist("aging")}
                  className={`text-xs font-semibold px-2.5 py-1 rounded ${
                    view === "aging" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid="ap-aging-toggle-aging"
                >
                  A/P Aging
                </button>
                <button
                  type="button"
                  onClick={() => setViewPersist("paid")}
                  className={`text-xs font-semibold px-2.5 py-1 rounded ${
                    view === "paid" ? "bg-emerald-600 text-white" : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid="ap-aging-toggle-paid"
                >
                  Paid
                </button>
              </div>
            </div>
          </div>

          {view === "aging" && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {BUCKETS.map(b => {
                  const amt = aging.buckets[b.key] || 0;
                  const pct = aging.total ? (amt / aging.total) * 100 : 0;
                  const isActive = bucket === b.key;
                  return (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => onBucketClick(b.key)}
                      className={`text-left rounded-lg border p-3 transition ${BG[b.color]} hover:shadow-md hover:-translate-y-0.5 ${isActive ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                      data-testid={`ap-aging-bucket-${b.key}`}
                      aria-pressed={isActive}
                      title={isActive ? "Click again to clear filter" : `Filter bills to ${b.label}`}
                    >
                      <div className={`text-[10px] uppercase tracking-wider font-semibold ${TEXT[b.color]}`}>{b.label}</div>
                      <div className={`font-mono-num text-lg font-semibold mt-0.5 ${TEXT[b.color]}`}>{fmtMoney(amt)}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{b.desc}</div>
                      <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full ${BAR[b.color]} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className={`text-[10px] mt-1 ${TEXT[b.color]}`}>{pct.toFixed(0)}% of A/P{isActive ? " · filtered" : ""}</div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4">
                <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
                  {BUCKETS.map(b => {
                    const amt = aging.buckets[b.key] || 0;
                    const pct = aging.total ? (amt / aging.total) * 100 : 0;
                    if (pct === 0) return null;
                    return <div key={b.key} className={BAR[b.color]} style={{ width: `${pct}%` }} title={`${b.label}: ${fmtMoney(amt)}`} />;
                  })}
                </div>
              </div>
            </>
          )}

          {view === "highlights" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ap-highlights">
              {/* Overdue tile — clicking filters the list to strictly-
                  late bills with a balance still due. */}
              <button
                type="button"
                onClick={() => { const p = new URLSearchParams(params); p.set("overdue", "1"); p.delete("bucket"); setParams(p, { replace: true }); }}
                className="text-left rounded-lg border border-rose-200 bg-rose-50 p-4 hover:shadow-md hover:-translate-y-0.5 transition"
                data-testid="ap-highlight-overdue"
              >
                <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700">Overdue · click to filter</div>
                <div className="font-mono-num text-2xl font-bold mt-1 text-rose-800">
                  {fmtMoney(
                    (aging.buckets["1_30"]  || 0) +
                    (aging.buckets["31_60"] || 0) +
                    (aging.buckets["61_90"] || 0) +
                    (aging.buckets["over_90"]|| 0)
                  )}
                </div>
                <div className="text-[11px] text-rose-700 mt-1">Past-due bills need payment.</div>
              </button>
              {/* Due-within-30-days tile — routes to the same bucket
                  filter Aging view uses so a click here == click "current". */}
              <button
                type="button"
                onClick={() => { const p = new URLSearchParams(params); p.set("bucket", "current"); p.delete("overdue"); setParams(p, { replace: true }); }}
                className="text-left rounded-lg border border-amber-200 bg-amber-50 p-4 hover:shadow-md hover:-translate-y-0.5 transition"
                data-testid="ap-highlight-due-soon"
              >
                <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">Due within 30 days · click to filter</div>
                <div className="font-mono-num text-2xl font-bold mt-1 text-amber-800">
                  {fmtMoney(aging.buckets["current"] || 0)}
                </div>
                <div className="text-[11px] text-amber-700 mt-1">Not yet late — pay soon to stay current.</div>
              </button>
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border bg-white overflow-hidden">
        {/* Client-side search input — filters the bills list by number,
             vendor, status, or amount. Kept inside the table container
             so it visually anchors above the header row. */}
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/40">
          <div className="relative max-w-md">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bills — number, vendor, status…"
              className="w-full pl-8 pr-8 py-1.5 rounded-md border border-slate-300 text-xs focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
              data-testid="bills-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
                data-testid="bills-search-clear"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        {(outstanding || overdue || asOf || bucket) && (
          <div
            className="flex items-center justify-between px-3 py-2 bg-cyan-50 border-b border-cyan-100 text-xs text-cyan-900"
            data-testid="bills-filter-chip"
          >
            <span>
              Showing{" "}
              {outstanding && <b>outstanding</b>}
              {outstanding && overdue && " · "}
              {overdue && <b>overdue</b>}
              {bucket && <b>{BUCKETS.find(b => b.key === bucket)?.label || bucket}</b>}
              {(outstanding || overdue || bucket) && asOf && " "}
              {asOf && <>as of <b className="font-mono-num">{asOf}</b></>}
              {" "}·{" "}
              <span className="font-mono-num">{filtered.length}</span> of {items.length}
            </span>
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-cyan-700 hover:underline"
              data-testid="bills-clear-filters"
            >
              <X size={12} /> Clear filters
            </button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b">
            <tr><th className="px-3 py-2 text-left">Number</th><th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-left">Issued</th><th className="px-3 py-2 text-left">Due</th>
              <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2">Status</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map(b => (
              <tr key={b.id} className="border-b hover:bg-slate-50">
                <td className="px-3 py-2 font-mono-num text-slate-600">
                  {numEditId === b.id ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        autoFocus
                        value={numEditVal}
                        onChange={(e) => setNumEditVal(e.target.value)}
                        onBlur={() => commitNumberEdit(b)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitNumberEdit(b);
                          if (e.key === "Escape") setNumEditId(null);
                        }}
                        className="w-28 border rounded px-1.5 py-0.5 text-xs font-mono-num"
                        data-testid={`bill-number-input-${b.id}`}
                      />
                      <Check size={12} className="text-emerald-600" />
                    </span>
                  ) : (
                    <button
                      onClick={() => { setNumEditId(b.id); setNumEditVal(b.number || ""); }}
                      className="text-slate-700 hover:text-indigo-600 hover:underline decoration-dotted"
                      title="Click to edit"
                      data-testid={`bill-number-${b.id}`}
                    >{b.number}</button>
                  )}
                </td>
                <td className="px-3 py-2">{b.contact_name}</td>
                <td className="px-3 py-2 font-mono-num text-slate-500">{fmtDate(b.issue_date)}</td>
                <td className="px-3 py-2 font-mono-num text-slate-500">{fmtDate(b.due_date)}</td>
                <td className="px-3 py-2 text-right font-mono-num">{fmtMoney(b.total)}</td>
                <td className="px-3 py-2 text-right font-mono-num">{fmtMoney(b.balance_due)}</td>
                <td className="px-3 py-2"><span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100">{b.status}</span></td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button data-testid={`bill-memorize-btn-${b.id}`} onClick={() => setMemorizing(b)}
                            title="Memorize (recurring)"
                            className="p-1 rounded hover:bg-fuchsia-100 text-fuchsia-600"><Repeat size={13} /></button>
                    <button data-testid="bill-edit-btn" onClick={() => navigate(`/bills/${b.id}/edit`)}
                            className="p-1 rounded hover:bg-indigo-100 text-indigo-600"><Pencil size={13} /></button>
                    <button onClick={() => del(b.id)} className="p-1 rounded hover:bg-red-100 text-red-500"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={8} className="text-center py-8 text-slate-500">
                  {(outstanding || asOf || bucket)
                    ? `No matching bills${items.length ? ` (${items.length} total, none met the filter)` : ""}.`
                    : "No bills."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {creating && <BillModal contacts={contacts} itemsCatalog={itemsCatalog} currentId={currentId} prefill={creatingPrefill}
                                onClose={() => { setCreating(false); setCreatingPrefill(null); load(); }} />}
      {editing && <BillModal contacts={contacts} itemsCatalog={itemsCatalog} currentId={currentId} bill={editing} onClose={() => { setEditing(null); load(); }} />}
      {memorizing && <MemorizeModal currentId={currentId} source={memorizing} kind="bill" onClose={() => setMemorizing(null)} />}
    </div>
  );
}

function BillModal({ contacts, itemsCatalog, currentId, bill, prefill, onClose }) {
  const fmtMoney = useMoneyFmt();
  const editMode = !!bill;
  const p = prefill || {};
  const initLines = () => {
    if (bill?.line_items?.length) return bill.line_items.map(l => ({ ...l }));
    if (p.amount || p.description) {
      const amt = Number(p.amount || 0);
      return [{
        description: p.description || "Services",
        quantity: 1, rate: amt, amount: amt,
      }];
    }
    return [{ description: "", quantity: 1, rate: 0, amount: 0 }];
  };
  const [contact, setContact] = useState(bill?.contact_id || p.contact_id || "");
  useEffect(() => {
    if (!contact && p.contact_id && contacts.some(c => c.id === p.contact_id)) {
      setContact(p.contact_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, p.contact_id]);
  const [issue, setIssue] = useState(bill?.issue_date || p.issue_date || new Date().toISOString().slice(0, 10));
  const [due, setDue] = useState(
    bill?.due_date
    || p.due_date
    || new Date(Date.now() + (Number(p.due_days) || 30) * 86400000).toISOString().slice(0, 10)
  );
  const [lines, setLines] = useState(initLines);
  const [status, setStatus] = useState(bill?.status || p.status || "open");
  const [number, setNumber] = useState(bill?.number || "");
  const upd = (i, p) => setLines(lines.map((x, j) => j === i ? { ...x, ...p, amount: (p.quantity !== undefined ? p.quantity : x.quantity) * (p.rate !== undefined ? p.rate : x.rate) } : x));
  const total = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const save = async () => {
    const c = contacts.find(x => x.id === contact);
    const body = {
      contact_id: contact || null, contact_name: c?.name || bill?.contact_name || "",
      issue_date: issue, due_date: due, line_items: lines, status,
    };
    if (number && number !== bill?.number) body.number = number.trim();
    if (editMode) {
      const r = await api.patch(`/companies/${currentId}/bills/${bill.id}`, body);
      if (r.data?.number_conflict) {
        toast.warning(`Heads up — another bill already uses ${body.number}.`);
      } else {
        toast.success("Bill updated");
      }
    } else {
      if (number) body.number = number.trim();
      await api.post(`/companies/${currentId}/bills`, body);
      toast.success("Bill created");
    }
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold">{editMode ? `Edit Bill ${bill.number}` : "New Bill"}</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <select value={contact} onChange={(e) => setContact(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
            <option value="">Vendor…</option>
            {contacts.filter(c => c.type === "vendor" || c.type === "both").map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
            <option value="open">Open</option><option value="partial">Partial</option><option value="paid">Paid</option>
          </select>
        </div>
        <div>
          <label htmlFor={`bill-num-${bill?.id || "new"}`}
                 className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">
            Bill number
          </label>
          <input
            id={`bill-num-${bill?.id || "new"}`}
            key={`bill-num-${bill?.id || "new"}`}
            type="text"
            autoComplete="off"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="e.g. BILL-2001 (leave blank to auto-assign)"
            className="w-full border rounded px-2 py-1.5 text-sm font-mono-num focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
            data-testid="bill-modal-number"
          />
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <div className="col-span-5">
                <ItemPicker
                  items={itemsCatalog}
                  value={l.description}
                  onChangeText={(txt) => upd(i, { description: txt })}
                  onPickItem={(it) => upd(i, {
                    item_id: it.id,
                    item_name: it.name,
                    description: it.name,
                    rate: Number(it.price || 0),
                    // Bills use the item's expense-account mapping — falls
                    // back to income_account when the user hasn't set one.
                    expense_account_id: it.expense_account_id || null,
                    expense_account_name: it.expense_account_name || "",
                    category: it.expense_account_name || it.income_account_name || "",
                  })}
                  testId={`bill-line-${i}`}
                />
              </div>
              <input type="number" value={l.quantity} onChange={(e) => upd(i, { quantity: Number(e.target.value) })} className="col-span-2 border rounded px-2 py-1.5 text-sm font-mono-num" />
              <input type="number" value={l.rate} onChange={(e) => upd(i, { rate: Number(e.target.value) })} className="col-span-2 border rounded px-2 py-1.5 text-sm font-mono-num" />
              <div className="col-span-2 py-1.5 text-right font-mono-num">{fmtMoney(l.amount)}</div>
              <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="col-span-1 text-red-500"><Trash2 size={13} /></button>
            </div>
          ))}
          <button onClick={() => setLines([...lines, { description: "", quantity: 1, rate: 0, amount: 0 }])}
                  className="text-xs text-slate-600 border border-dashed rounded px-2 py-1">+ Line</button>
        </div>
        <div className="flex justify-end gap-4 items-center border-t pt-3">
          <div className="text-lg font-mono-num font-semibold">Total: {fmtMoney(total)}</div>
          <button data-testid={TID.saveBtn} onClick={save} className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm">
            {editMode ? "Save changes" : "Save Bill"}
          </button>
        </div>
      </div>
    </div>
  );
}
